import React, {
  useState,
  createContext,
  useContext,
  ReactNode,
  useCallback,
} from "react";
import ReactDOM from "react-dom";

interface DialogConfig<T = any> {
  component: React.ComponentType<DialogComponentProps<T>>;
  panelClass?: string[];
  width?: string;
  height?: string;
  position?: { top?: string; left?: string };
  data?: T;
  onClose?: (result: any) => void;
}

interface DialogComponentProps<T = any> {
  data?: T;
  onClose: (result: any) => void;
}

interface DialogContextProps {
  openDialog: <T = any>(config: DialogConfig<T>) => Promise<any>;
}

const DialogContext = createContext<DialogContextProps | null>(null);

export const useDialog = (): DialogContextProps => {
  const context = useContext(DialogContext);
  if (!context) {
    throw new Error("useDialog must be used within a DialogProvider");
  }
  return context;
};

export const DialogProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [dialogConfig, setDialogConfig] = useState<DialogConfig | null>(null);

  const openDialog = useCallback(
    <T,>(config: Omit<DialogConfig<T>, "onClose">): Promise<any> => {
      return new Promise((resolve) => {
        setDialogConfig({
          ...config,
          onClose: (result: any) => {
            setDialogConfig(null);
            resolve(result);
          },
        });
      });
    },
    []
  );

  return (
    <DialogContext.Provider value={{ openDialog }}>
      {children}
      {dialogConfig && dialogConfig.onClose && (
        <Dialog {...dialogConfig} onClose={dialogConfig.onClose} />
      )}
    </DialogContext.Provider>
  );
};

const Dialog: React.FC<DialogConfig & { onClose: (result: any) => void }> = ({
  component: Component,
  panelClass = [],
  width = "50%",
  height = "auto",
  position,
  data,
  onClose,
}) => {
  return ReactDOM.createPortal(
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50"
      onClick={() => onClose(null)}
    >
      <div
        className={`bg-white shadow-lg rounded-lg overflow-hidden ${panelClass.join(
          " "
        )}`}
        style={{
          width,
          height,
          position: "relative",
          ...position,
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <Component data={data} onClose={onClose} />
      </div>
    </div>,
    document.body
  );
};
